// 학생 사이드바 컴포넌트 - 축소된 메뉴

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Box,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import SettingsIcon from '@mui/icons-material/Settings';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import LogoutIcon from '@mui/icons-material/Logout';
import { useStudentAuth } from '../../hooks/useStudentAuth';
import { getAccessToken } from '../../../../lib/authUtils';

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  scheduleType?: 'weekly' | 'period';
}

// 학생용 축소된 메뉴
const allMenuItems: MenuItem[] = [
  { id: 'attendance', label: '출결 현황', icon: <CheckCircleIcon />, path: '/student/attendance' },
  { id: 'study-time', label: '순공부시간', icon: <AccessTimeIcon />, path: '/student/study-time' },
  { id: 'penalty', label: '벌점 현황', icon: <WarningAmberIcon />, path: '/student/penalty' },
  { id: 'applications', label: '신청', icon: <RestaurantIcon />, path: '/student/applications' },
  { id: 'chat', label: '선생님 메시지', icon: <ChatBubbleOutlineIcon />, path: '/student/chat' },
  { id: 'schedule', label: '주간 스케줄', icon: <CalendarMonthIcon />, path: '/student/schedule', scheduleType: 'weekly' },
  { id: 'period-schedule', label: '교시 스케줄', icon: <CalendarMonthIcon />, path: '/student/period-schedule', scheduleType: 'period' },
];

export default function StudentSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { student, signOut } = useStudentAuth();
  const [scheduleType, setScheduleType] = useState<string>('period');

  useEffect(() => {
    loadScheduleType();
  }, [student?.organization_id]);

  const loadScheduleType = async () => {
    try {
      const orgId = student?.organization_id;
      if (!orgId) return;

      const token = await getAccessToken();
      if (!token) return;

      const response = await fetch(`/api/v1/owner/organization/${orgId}/schedule-type`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return;

      const data = await response.json();
      setScheduleType(data.schedule_type || 'weekly');
    } catch (err) {
      console.error('스케줄 유형 로드 실패:', err);
    }
  };

  // schedule_type에 따라 메뉴 필터링
  const menuItems = allMenuItems.filter((item) => {
    if (!item.scheduleType) return true;
    return item.scheduleType === scheduleType;
  });

  const handleMenuClick = (path: string) => {
    navigate(path);
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('로그아웃 에러:', error);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* 사용자 정보 */}
      <Box sx={{ p: 2, backgroundColor: '#2196f3', color: 'white' }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {student?.name || '학생'}님
        </Typography>
        <Typography variant="caption">{student?.phone}</Typography>
        {student?.organization_name && (
          <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>
            {student.organization_name}
          </Typography>
        )}
      </Box>

      <Divider />

      {/* 메뉴 목록 */}
      <List sx={{ flexGrow: 1, pt: 2 }}>
        {menuItems.map((item) => {
          const isActive = location.pathname.startsWith(item.path);

          return (
            <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => handleMenuClick(item.path)}
                selected={isActive}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  '&.Mui-selected': {
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: '#e3f2fd',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    color: isActive ? '#1976d2' : 'text.secondary',
                    minWidth: 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: isActive ? 600 : 400,
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider />

      {/* 하단 메뉴 */}
      <List sx={{ pb: 2 }}>
        <ListItem disablePadding>
          <ListItemButton onClick={() => navigate('/student/settings')} sx={{ mx: 1, borderRadius: 1 }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary="설정" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout} sx={{ mx: 1, borderRadius: 1 }}>
            <ListItemIcon sx={{ minWidth: 40 }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="로그아웃" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );
}
